import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, ZoomPanControlButton } from '@fs/zion-ui'
import { DocumentInfo, ControlFullscreen, ControlFullscreenExit } from '@fs/zion-icon'
import { useQueryParams } from '@fs/zion-router'
import { css } from '@emotion/core'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import { useGroupData, useImageOrganizeModes } from '../../views/imageBrowse/hooks'
import useFullscreenState from '../../../shared/hooks/useFullscreenState'

const selectStyles = css`
  margin-top: -1px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;

  & div {
    border: none;
  }

  & select:focus {
    margin-top: 1px;
    margin-inline-end: 1px;
  }

  & select {
    box-shadow:
      0px 1px 5px 0px rgba(0, 0, 0, 0.2),
      0px 2px 2px 0px rgba(0, 0, 0, 0.14),
      0px 3px 1px -2px rgba(0, 0, 0, 0.12);
  }
`

export default function Controls({ viewMode, setViewMode }) {
  const [t] = useTranslation()
  const [isFullScreen, toggleFullscreen] = useFullscreenState()
  const { group, initialGroup, itemCount, itemNumber, setGroupIndex } = useGroupData()
  const { isImageOrganizeMode } = useImageOrganizeModes()
  const { query } = useQueryParams()

  const isVolumeSet = initialGroup?.types?.includes('VOLUME_SET')

  const selectOptions = useMemo(() => {
    const options = []

    if (isVolumeSet) {
      initialGroup.childGroups.forEach((_, index) => {
        options.push({
          value: `volume-${index}`,
          label: t('sharedViewer.viewChildVolume', 'Volume {volumeNumber}', { volumeNumber: index + 1 }),
        })
      })
    } else {
      options.push({
        value: 'single',
        label: t('sharedViewer.viewIndividualImage', 'View item {itemNumber} by itself', { itemNumber }),
      })
    }

    options.push({
      value: 'group',
      label: t('sharedViewer.viewWholeGroup', 'View image group'),
    })

    return options
  }, [initialGroup.childGroups, isVolumeSet, itemNumber, t])

  const onChange = ({ target: { value } }) => {
    if (isVolumeSet && value !== 'group') {
      const groupIndex = value.split('-')[1]
      setGroupIndex(groupIndex)
      setViewMode('single')
    } else {
      setViewMode(value)
    }
  }

  const selectProps = {
    options: selectOptions,
    value:
      isVolumeSet && viewMode !== 'group'
        ? `volume-${initialGroup.childGroups.indexOf(initialGroup.childGroups.find((g) => g.id === group.id))}`
        : viewMode,
    onChange,
    hidePlaceholder: true,
    name: 'view-image-by-itself-button',
  }

  return (
    <>
      <ZoomPanControlButton
        label={t('shared-image-viewer.toggle-fullscreen', 'Toggle fullscreen mode')}
        Icon={isFullScreen ? ControlFullscreenExit : ControlFullscreen}
        onClick={toggleFullscreen}
        linkName="fullscreen-button"
      />
      <ControlButton />
      {!isImageOrganizeMode && itemCount > 1 && (!isVolumeSet || query.grid === 'on') && (
        <div css={selectStyles}>
          <Select {...selectProps} label={false} aria-label={t('imageViewer.viewMode', 'Select a view mode.')} />
        </div>
      )}
    </>
  )
}

function ControlButton() {
  const { infoSheetOverlay } = useOverlayContext()
  const [t] = useTranslation()
  return !infoSheetOverlay?.isOpen ? (
    <ZoomPanControlButton
      Icon={DocumentInfo}
      onClick={() => {
        infoSheetOverlay.handleClick()
      }}
      label={t('open-infosheet.button', 'Open Panel')}
      linkName="open-panel-button"
    />
  ) : null
}
